import { useState, useEffect, useCallback, useRef, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { wattelEMApi, DeviceOverview } from '../api/wattelEM';
import { architectureApi, InstallationData, CatalogDevice } from '../api/architecture';
import { displaySn } from '../utils/deviceDisplay';
import { useAuth } from '../context/AuthContext';
import SuperAdminHome from './home/SuperAdminHome';
import InstallerHome from './home/InstallerHome';
import EnergyFlowBackground from '../components/EnergyFlowBackground';
import { useI18n } from '../i18n';
import { displayDefaultName } from '../i18n/defaultNames';
import { HomeWidgets, ZoneConsumption, useEnergyToday, installedSns } from '../components/home/HomeWidgets';

// ---- Schema layout constants (px) ----
const CARD_H = 72;                       // height of every main node card
const CARD_CENTER = CARD_H / 2;          // vertical center of a card (wire anchor)
const EQ_H = 60;                         // height of an equipment card
const EQ_GAP = 10;                       // gap between equipment cards
const EQ_OFFSET = (CARD_H - EQ_H) / 2;   // equipment column top offset, so the first card centers on its zone
const ROW_GAP = 14;                      // gap between sibling rows

// ---- Schema node palette ----
// Each key maps to a `--sn-a-<key>` RGB triplet defined in index.css (.sn-root, light)
// and redefined under html.dark (ZOS dark theme), so the schema follows the theme toggle.
type AccentKey = 'installation' | 'linky' | 'tableau' | 'wem' | 'zone' | 'tableau-eq' | 'go' | 'wall' | 'prise' | 'eq';
const accentColor = (k: AccentKey) => `rgb(var(--sn-a-${k}))`;
const accentVars = (k: AccentKey) => ({ '--accent': `var(--sn-a-${k})` }) as CSSProperties;

const equipAccent = (eqType: string): AccentKey =>
  eqType === 'WattelGo' ? 'go' : eqType === 'WattelWall' ? 'wall' : eqType === 'Prise 16A' ? 'prise' : 'eq';

const equipCode = (eqType: string) =>
  eqType === 'WattelGo' ? 'GO' : eqType === 'WattelWall' ? 'WL' : eqType === 'Prise 16A' ? '16A' : 'EQ';

const SEG_BTN = 'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-xs font-semibold text-blue-100 hover:text-white hover:bg-white/10 active:scale-[0.97] transition-[color,background-color,transform] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70';

function usePrefersReducedMotion() {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches);
  useEffect(() => {
    const mql = window.matchMedia?.(query);
    if (!mql) return;
    const onChange = () => setReduced(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function SnIcon({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {children}
    </svg>
  );
}

function SnChevron() {
  return (
    <span className="sn-chevron" aria-hidden>
      <SnIcon><path d="M9 6l6 6-6 6" /></SnIcon>
    </span>
  );
}

/** Status pill; `tone` picks the colour, `label` is the translated text. */
function SnPill({ tone, label, ping = false }: { tone: 'ok' | 'err' | 'info' | 'neutral'; label: string; ping?: boolean }) {
  return (
    <span className={`sn-pill is-${tone}`}>
      {tone !== 'info' && (
        <span className="relative flex h-1.5 w-1.5">
          {ping && tone === 'ok' && <span className="sn-pill-dot absolute inset-0 opacity-70 animate-ping motion-reduce:animate-none" />}
          <span className="sn-pill-dot relative h-1.5 w-1.5" />
        </span>
      )}
      {label}
    </span>
  );
}

/** Orthogonal wire from (0, sy) to (w, cy) with a rounded elbow at x = jx. */
function wirePath(sy: number, cy: number, jx: number, w: number, radius = 10) {
  if (Math.abs(cy - sy) < 0.5) return `M 0 ${sy} H ${w}`;
  const dir = cy > sy ? 1 : -1;
  const r = Math.min(radius, Math.abs(cy - sy) / 2, jx);
  return `M 0 ${sy} H ${jx - r} Q ${jx} ${sy} ${jx} ${sy + dir * r} V ${cy - dir * r} Q ${jx} ${cy} ${jx + r} ${cy} H ${w}`;
}

/**
 * Animated fan-out connector. `centers` holds the exact vertical center (px) of
 * each target card, so the branches stay aligned even when some rows are
 * expanded (variable height). Branches flagged `live: false` are drawn as a
 * static dashed wire (no energy flowing). Colours are CSS colours (theme vars),
 * applied through `style` because SVG presentation attributes don't resolve var().
 */
function SchemaFanout({ centers, width, jx = 28, from, to, live, idKey }: {
  centers: number[];
  width: number;
  jx?: number;
  from: string;
  to: string | string[];
  live?: boolean[];
  idKey: string;
}) {
  const reduceMotion = usePrefersReducedMotion();
  if (centers.length === 0) return null;
  const sy = centers[0];
  const height = Math.max(...centers) + 8;
  const colorAt = (i: number) => (Array.isArray(to) ? to[i] ?? to[0] : to);
  const isLive = (i: number) => live?.[i] ?? true;
  const gid = (i: number) => `sn-${idKey}-${i}`;
  const paths = centers.map(cy => wirePath(sy, cy, jx, width));
  return (
    <div className="relative z-[1] shrink-0 flex items-start sn-fade" aria-hidden>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
        <defs>
          {centers.map((cy, i) => (
            <linearGradient key={i} id={gid(i)} gradientUnits="userSpaceOnUse" x1="0" y1={cy} x2={width} y2={cy}>
              <stop offset="0%" style={{ stopColor: from }} />
              <stop offset="100%" style={{ stopColor: colorAt(i) }} />
            </linearGradient>
          ))}
        </defs>
        {/* Offline branches: static dashed wire */}
        {paths.map((d, i) => !isLive(i) && (
          <path key={`off-${i}`} className="sn-wire-off" d={d} strokeWidth="1.5" strokeDasharray="2 5" strokeLinecap="round" fill="none" />
        ))}
        {/* Live branches: soft base (grouped so shared trunks don't stack opacity) + flowing dashes */}
        <g className="sn-wire-base">
          {paths.map((d, i) => isLive(i) && (
            <path key={`base-${i}`} d={d} stroke={`url(#${gid(i)})`} strokeWidth="2" strokeLinecap="round" fill="none" />
          ))}
        </g>
        {paths.map((d, i) => isLive(i) && (
          <path key={`flow-${i}`} className="sn-flow" d={d} stroke={`url(#${gid(i)})`} strokeWidth="2" strokeLinecap="round" fill="none" />
        ))}
        {!reduceMotion && paths.map((d, i) => isLive(i) && (
          <g key={`dot-${i}`} style={{ color: colorAt(i) }}>
            <circle r="5" className="sn-spark-halo">
              <animateMotion dur="2.4s" begin={`${-i * 0.35}s`} repeatCount="indefinite" path={d} />
            </circle>
            <circle r="2" className="sn-spark-core">
              <animateMotion dur="2.4s" begin={`${-i * 0.35}s`} repeatCount="indefinite" path={d} />
            </circle>
          </g>
        ))}
        {/* Ports */}
        <circle cx="0" cy={sy} r="3.5" className="sn-port" strokeWidth="1.5" style={{ stroke: from }} />
        {centers.map((cy, i) => (
          <circle key={`port-${i}`} cx={width} cy={cy} r="3.5" className={`sn-port ${isLive(i) ? '' : 'is-off'}`} strokeWidth="1.5" style={isLive(i) ? { stroke: colorAt(i) } : undefined} />
        ))}
      </svg>
    </div>
  );
}


function Dashboard() {
  const { user, currentMembership } = useAuth();
  const isSupportMode = typeof window !== 'undefined' && !!sessionStorage.getItem('actingClientOrgId');

  // In support mode, always show the client dashboard (whoever is impersonating).
  if (isSupportMode) return <ClientSmartBuildingDashboard />;

  // Role-based home: super_admin and installer get dedicated landing pages
  if (user?.platformRole === 'super_admin') return <SuperAdminHome />;
  if (currentMembership?.role === 'installer_admin' || currentMembership?.role === 'installer_technician') {
    return <InstallerHome />;
  }

  return <ClientSmartBuildingDashboard />;
}

function ClientSmartBuildingDashboard() {
  const navigate = useNavigate();
  const { t, tp } = useI18n();
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState<DeviceOverview[]>([]);
  const [installations, setInstallations] = useState<InstallationData[]>([]);
  const [catalogDevices, setCatalogDevices] = useState<CatalogDevice[]>([]);

  // Dropdowns on KPI cards
  const [showInstDropdown, setShowInstDropdown] = useState(false);
  const [showEquipDropdown, setShowEquipDropdown] = useState(false);
  const [showZoneDropdown, setShowZoneDropdown] = useState(false);

  // Selected installation in the schema viewer (if multiple)
  const [selectedInstId, setSelectedInstId] = useState<number | null>(null);

  // Tree expand state for schema
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Close dropdowns on click outside
  const heroRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (heroRef.current && !heroRef.current.contains(e.target as Node)) {
        setShowInstDropdown(false);
        setShowEquipDropdown(false);
        setShowZoneDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);



  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [d, i, c] = await Promise.allSettled([
      wattelEMApi.getOverview(),
      architectureApi.getInstallations().then(r => r.data),
      architectureApi.getCatalogDevices().then(r => r.data),
    ]);
    if (d.status === 'fulfilled') setDevices(d.value);
    if (i.status === 'fulfilled') setInstallations(i.value);
    if (c.status === 'fulfilled') setCatalogDevices(c.value);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Énergie du jour des WattelEM installés : widgets du bandeau et consommation par zone
  const energy = useEnergyToday(installedSns(installations));

  // Auto-select first installation when loaded
  useEffect(() => {
    if (installations.length > 0 && selectedInstId === null) {
      setSelectedInstId(installations[0].id);
    }
  }, [installations, selectedInstId]);

  const toggle = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const expandAll = () => {
    const inst = installations.find(i => i.id === selectedInstId);
    if (!inst) return;
    const regularZones = inst.zones.filter(z => !z.name.startsWith('Tableau'));
    const tableauZones = inst.zones.filter(z => z.name.startsWith('Tableau'));
    const tableauWemSns = tableauZones.flatMap(z => (z.wattelemSn || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean));
    const wemKeys = devices.filter(d => tableauWemSns.includes(d.device_sn.toLowerCase())).map(d => `wem-${d.device_sn}`);
    const keys = [`tableau-${inst.id}`, ...wemKeys, ...regularZones.map(z => `zone-${z.id}`)];
    setExpanded(new Set(keys));
  };

  const collapseAll = () => setExpanded(new Set());

  // KPIs
  const totalInstallations = installations.length;
  const allEquipment = installations.flatMap(inst =>
    inst.zones.flatMap(z => (z.equipment || []).map(eq => ({
      ...eq, zoneName: z.name, installationName: inst.name, installationId: inst.id,
    })))
  );
  const totalEquipment = allEquipment.length;

  // All zones flattened — the Tableau Électrique is stored as a zone but is not one
  const allZones = installations.flatMap(inst =>
    inst.zones.filter(z => !z.name.startsWith('Tableau'))
      .map(z => ({ ...z, installationName: inst.name, installationId: inst.id }))
  );
  const totalZones = allZones.length;

  // Devices stats
  const allInstSns = new Set(installations.flatMap(inst =>
    inst.zones.flatMap(z => (z.wattelemSn || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean))
  ));
  const installedDevices = allInstSns.size > 0
    ? devices.filter(d => allInstSns.has(d.device_sn.toLowerCase()))
    : devices;
  const onlineDevicesCount = installedDevices.filter(d => d.online).length;
  void onlineDevicesCount;

  // Equipment status
  const getEquipStatus = (eq: any) => {
    if (eq.deviceSn) {
      const dev = devices.find(d => d.device_sn.toLowerCase() === (eq.deviceSn || '').toLowerCase());
      if (dev) return dev.online ? 'online' : 'offline';
    }
    if (eq.deviceId) {
      const cat = catalogDevices.find(c => c.deviceId === eq.deviceId);
      if (cat?.deviceStatus === 'Available' || cat?.deviceStatus === 'active') return 'online';
      if (cat?.deviceStatus) return 'offline';
    }
    return 'unknown';
  };

  const getEquipType = (eq: any) => {
    const cat = eq.deviceId ? catalogDevices.find(c => c.deviceId === eq.deviceId) : null;
    const model = (cat?.chargePointModel || eq.type || '').toLowerCase();
    if (model.includes('go')) return 'WattelGo';
    if (model.includes('wall')) return 'WattelWall';
    if (model.includes('em') || model.includes('wem')) return 'WattelEM';
    if (model.includes('prise') || model.includes('16a') || model.includes('16 a')) return 'Prise 16A';
    return eq.type || 'Équipement';
  };
  // Affichage seul : la valeur renvoyée par getEquipType reste celle comparée dans le code.
  const eqTypeLabel = (ty: string) => ty === 'Prise 16A' ? t('arch2_socket_16a') : ty === 'Équipement' ? t('home_equipmentType') : ty;

  const Skeleton = ({ className = '' }: { className?: string }) => (
    <div className={`animate-pulse bg-white/30 rounded-lg ${className}`} />
  );

  // Currently displayed installation in the schema
  const currentInst = installations.find(i => i.id === selectedInstId) || null;

  return (
    <div className="space-y-5">
      {/* ============ COMMAND CENTER HERO ============ */}
      <div ref={heroRef} className="relative rounded-3xl shadow-2xl zce-fade-in pb-2">
        {/* Decorative layers wrapped in their own overflow-hidden so dropdowns can escape */}
        <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
          {/* Photo de fond — smart building */}
          <img
            src="https://www.getgrooven.com/wp-content/uploads/2025/05/AdobeStock_607460547-1-2048x1152-1.jpeg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-zce-navy-dark/95 via-zce-navy/90 to-zce-navy-light/85 zce-gradient-anim" />
          <div className="absolute inset-0 bg-zce-navy-dark/30" />
          {/* Floating color orbs */}
          <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-cyan-500/20 blur-3xl zce-float-orb" />
          <div className="absolute -bottom-32 -right-16 w-96 h-96 rounded-full bg-blue-500/20 blur-3xl zce-float-orb" style={{ animationDelay: '-6s' }} />
          <div className="absolute top-1/2 left-1/3 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl zce-float-orb" style={{ animationDelay: '-12s' }} />
          {/* Réseau électrique animé : impulsions d'énergie du compteur vers les zones */}
          <EnergyFlowBackground className="opacity-90" />
          <div className="absolute inset-0 zce-shimmer" />
        </div>

        <div className="relative p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4 zce-fade-up">
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-400/40 rounded-xl blur-md zce-pulse-dot" />
                <img src="/logos/logo zce.png" alt={t('arch3_logo_alt')} className="relative h-10 sm:h-12 w-auto object-contain bg-white rounded-xl p-1.5 shadow-lg" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wider">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 zce-pulse-dot" />
                    {t('pg_live_badge')}
                  </span>
                  <span className="text-[10px] text-blue-100 font-semibold">{t('home_synced')}</span>
                </div>
                <h1 className="text-xl sm:text-3xl font-extrabold text-white tracking-tight leading-tight">
                  Smart <span className="bg-gradient-to-r from-cyan-300 via-blue-300 to-emerald-300 bg-clip-text text-transparent">Building</span>
                </h1>
                <p className="text-blue-100 text-xs sm:text-sm mt-0.5">{t('home_subtitle')}</p>
              </div>
            </div>
            <button
              onClick={fetchAll}
              disabled={loading}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20 hover:border-cyan-400/40 disabled:opacity-50 transition-all group self-start sm:self-auto"
            >
              <svg className={`w-4 h-4 transition-transform group-hover:rotate-180 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              <span className="hidden sm:inline">{t('home_refresh')}</span>
            </button>
            <button
              onClick={() => navigate(selectedInstId ? `/rapport-rse?installationId=${selectedInstId}` : '/rapport-rse')}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl bg-emerald-500/90 backdrop-blur-xl border border-emerald-400/30 text-white hover:bg-emerald-600 transition-all shadow-md self-start sm:self-auto"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              <span className="hidden sm:inline">{t('home_rse')}</span>
            </button>
          </div>

          {/* ============ WIDGETS : puissance, énergie du jour, parc, alertes, CO₂ ============ */}
          <HomeWidgets devices={devices} installations={installations} energy={energy} loading={loading} />

          {/* ============ KPI CARDS WITH DROPDOWNS (3 cards) ============ */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-5">

            {/* === Card 1: Installations === */}
            <div className="relative">
              <button
                onClick={() => { setShowInstDropdown(v => !v); setShowEquipDropdown(false); setShowZoneDropdown(false); }}
                className="w-full text-left relative bg-white/10 backdrop-blur-2xl rounded-2xl p-4 border border-white/25 shadow-xl ring-1 ring-white/10 zce-fade-up zce-delay-2 zce-card-hover hover:bg-white/15 transition-all"
              >
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-blue-400/10 blur-2xl" />
                <div className="relative">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="inline-flex p-1.5 rounded-lg bg-blue-500/15 border border-blue-400/20">
                      <svg className="w-4 h-4 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                    </span>
                    <svg className={`w-4 h-4 text-blue-300/70 transition-transform ${showInstDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                  {loading ? <Skeleton className="h-8 w-12 mb-1" /> : (
                    <p className="text-3xl font-extrabold text-white tabular-nums tracking-tight">{totalInstallations}</p>
                  )}
                  <p className="text-xs text-white font-semibold mt-0.5">{t('home_installations')}</p>
                  <p className="text-[10px] text-blue-200 mt-1.5">{t('home_clickList')}</p>
                </div>
              </button>
            </div>

            {/* === Card 2: Équipements === */}
            <div className="relative">
              <button
                onClick={() => { setShowEquipDropdown(v => !v); setShowInstDropdown(false); setShowZoneDropdown(false); }}
                className="w-full text-left relative bg-white/10 backdrop-blur-2xl rounded-2xl p-4 border border-white/25 shadow-xl ring-1 ring-white/10 zce-fade-up zce-delay-3 zce-card-hover hover:bg-white/15 transition-all"
              >
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-violet-400/10 blur-2xl" />
                <div className="relative">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="inline-flex p-1.5 rounded-lg bg-violet-500/15 border border-violet-400/20">
                      <svg className="w-4 h-4 text-violet-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    </span>
                    <svg className={`w-4 h-4 text-violet-300/70 transition-transform ${showEquipDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                  {loading ? <Skeleton className="h-8 w-12 mb-1" /> : (
                    <p className="text-3xl font-extrabold text-white tabular-nums tracking-tight">{totalEquipment}</p>
                  )}
                  <p className="text-xs text-white font-semibold mt-0.5">{t('home_equipment')}</p>
                  <p className="text-[10px] text-blue-200 mt-1.5">{t('home_clickStatus')}</p>
                </div>
              </button>
            </div>

            {/* === Card 3: Zones === */}
            <div className="relative">
              <button
                onClick={() => { setShowZoneDropdown(v => !v); setShowInstDropdown(false); setShowEquipDropdown(false); }}
                className="w-full text-left relative bg-white/10 backdrop-blur-2xl rounded-2xl p-4 border border-white/25 shadow-xl ring-1 ring-white/10 zce-fade-up zce-delay-4 zce-card-hover hover:bg-white/15 transition-all"
              >
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-emerald-400/10 blur-2xl" />
                <div className="relative">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="inline-flex p-1.5 rounded-lg bg-emerald-500/15 border border-emerald-400/20">
                      <svg className="w-4 h-4 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z" /></svg>
                    </span>
                    <svg className={`w-4 h-4 text-emerald-300/70 transition-transform ${showZoneDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                  {loading ? <Skeleton className="h-8 w-12 mb-1" /> : (
                    <p className="text-3xl font-extrabold text-white tabular-nums tracking-tight">{totalZones}</p>
                  )}
                  <p className="text-xs text-white font-semibold mt-0.5">{t('home_zones')}</p>
                  <p className="text-[10px] text-blue-200 mt-1.5">{t('home_clickList')}</p>
                </div>
              </button>
            </div>

          </div>

          {/* Live status footer removed */}
        </div>
      </div>

      {/* ============ DROPDOWN PANELS (outside hero, z-[200], above schema) ============ */}
      {(showInstDropdown || showEquipDropdown || showZoneDropdown) && (
        <div className="relative z-[200] -mt-2">
          {/* Installations dropdown */}
          {showInstDropdown && (
            <div className="bg-slate-900 rounded-2xl border border-cyan-400/30 shadow-2xl p-3 animate-[slideDown_0.2s_ease-out]">
              <h3 className="text-xs font-bold text-cyan-300 uppercase tracking-wider px-2 mb-2">{t('home_installations')}</h3>
              {installations.length === 0 ? (
                <p className="px-2 py-3 text-sm text-blue-200 text-center">{t('home_noInstallation')}</p>
              ) : (
                <div className="space-y-1">
                  {installations.map(inst => {
                    const equipCount = inst.zones.reduce((s, z) => s + (z.equipment?.length ?? 0), 0);
                    return (
                      <button
                        key={inst.id}
                        onClick={() => { setSelectedInstId(inst.id); setShowInstDropdown(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-left ${selectedInstId === inst.id ? 'bg-cyan-500/15 border border-cyan-400/30' : 'hover:bg-white/5 border border-transparent'}`}
                      >
                        <span className="inline-flex p-2 rounded-lg bg-blue-500/20">
                          <svg className="w-4 h-4 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16" /></svg>
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-white">{inst.name}</p>
                          <p className="text-xs text-blue-200">{tp('count_zone', inst.zones.length)} · {tp('count_equipShort', equipCount)}</p>
                        </div>
                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${
                          inst.status === 'production' ? 'bg-emerald-500/20 text-emerald-300'
                            : inst.status === 'validated' ? 'bg-blue-500/20 text-blue-300'
                            : 'bg-white/10 text-white/70'
                        }`}>
                          {inst.status === 'production' ? t('status_production') : inst.status === 'validated' ? t('status_validated') : t('status_draft')}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Équipements dropdown */}
          {showEquipDropdown && (
            <div className="bg-slate-900 rounded-2xl border border-cyan-400/30 shadow-2xl p-3 max-h-80 overflow-y-auto animate-[slideDown_0.2s_ease-out]">
              <h3 className="text-xs font-bold text-cyan-300 uppercase tracking-wider px-2 mb-2">{t('home_equipment')} ({totalEquipment})</h3>
              {allEquipment.length === 0 ? (
                <p className="px-2 py-3 text-sm text-blue-200 text-center">{t('home_noEquipment')}</p>
              ) : (
                <div className="space-y-1">
                  {allEquipment.map((eq, idx) => {
                    const status = getEquipStatus(eq);
                    const eqType = getEquipType(eq);
                    const typeColor = eqType === 'WattelGo' ? 'bg-orange-500/20 text-orange-300 border-orange-500/30'
                      : eqType === 'WattelWall' ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30'
                      : eqType === 'Prise 16A' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                      : 'bg-violet-500/20 text-violet-300 border-violet-500/30';
                    return (
                      <button
                        key={`eq-dd-${eq.id ?? idx}`}
                        onClick={() => { if (eq.deviceId) navigate(`/wattel-em?deviceId=${eq.deviceId}`); setShowEquipDropdown(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 border border-transparent hover:border-white/10 transition-all text-left"
                      >
                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-lg border shrink-0 ${typeColor}`}>
                          {eqType === 'WattelGo' ? 'GO' : eqType === 'WattelWall' ? 'WL' : eqType === 'Prise 16A' ? '16A' : 'EQ'}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-white truncate">{displayDefaultName(eq.name, t, tp) || t('home_equipmentN', { n: idx + 1 })}</p>
                          <p className="text-xs text-blue-200 truncate">{eqTypeLabel(eqType)} · {eq.installationName} / {displayDefaultName(eq.zoneName, t, tp)}</p>
                        </div>
                        <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full shrink-0 ${
                          status === 'online' ? 'bg-emerald-500/20 text-emerald-300' : status === 'offline' ? 'bg-red-500/20 text-red-300' : 'bg-white/10 text-white/60'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${status === 'online' ? 'bg-emerald-400' : status === 'offline' ? 'bg-red-400' : 'bg-gray-400'}`} />
                          {status === 'online' ? t('status_online') : status === 'offline' ? t('status_offline') : '—'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Zones dropdown */}
          {showZoneDropdown && (
            <div className="bg-slate-900 rounded-2xl border border-cyan-400/30 shadow-2xl p-3 max-h-80 overflow-y-auto animate-[slideDown_0.2s_ease-out]">
              <h3 className="text-xs font-bold text-cyan-300 uppercase tracking-wider px-2 mb-2">{t('home_zones')} ({totalZones})</h3>
              {allZones.length === 0 ? (
                <p className="px-2 py-3 text-sm text-blue-200 text-center">{t('home_noZone')}</p>
              ) : (
                <div className="space-y-1">
                  {allZones.map(z => {
                    const equipCount = z.equipment?.length ?? 0;
                    const wemCount = (z.wattelemSn || '').split(',').map(s => s.trim()).filter(Boolean).length;
                    return (
                      <button
                        key={z.id}
                        onClick={() => { setSelectedInstId(z.installationId); setShowZoneDropdown(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 border border-transparent hover:border-white/10 transition-all text-left"
                      >
                        <span className="inline-flex p-2 rounded-lg bg-emerald-500/20">
                          <svg className="w-4 h-4 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" /></svg>
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-white truncate">{displayDefaultName(z.name, t, tp)}</p>
                          <p className="text-xs text-blue-200 truncate">{z.installationName}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300">{tp('count_equipShort', equipCount)}</span>
                          {wemCount > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">{wemCount} WEM</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ============ SCHEMA / ORGANIGRAMME ============ */}
      <div className="sn-root relative rounded-3xl overflow-hidden">
        {/* Header — navy brand bar, same in light and dark */}
        <div className="relative px-5 sm:px-6 py-4 flex items-center justify-between gap-4 flex-wrap overflow-hidden bg-gradient-to-r from-zce-navy-dark via-zce-navy to-zce-navy-light">
          <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
          <div className="pointer-events-none absolute -top-20 left-10 h-40 w-80 rounded-full bg-cyan-400/15 blur-3xl" />
          <div className="relative flex items-center gap-3 min-w-0">
            <span className="grid place-items-center h-10 w-10 shrink-0 rounded-xl bg-white/10 text-cyan-300 ring-1 ring-inset ring-white/20 shadow-[0_0_28px_-8px_rgba(34,211,238,0.7)]">
              <SnIcon className="w-5 h-5">
                <circle cx="5" cy="12" r="2" /><circle cx="19" cy="5" r="2" /><circle cx="19" cy="19" r="2" />
                <path d="M7 12h3a2 2 0 0 0 2-2V7a2 2 0 0 1 2-2h3M10 12a2 2 0 0 1 2 2v3a2 2 0 0 0 2 2h3" />
              </SnIcon>
            </span>
            <div className="min-w-0">
              <h2 className="text-white font-bold text-base leading-tight tracking-tight">{t('home_overview')}</h2>
              <p className="text-xs text-blue-100/80 mt-0.5 truncate">{t('home_overviewSub')}</p>
            </div>
          </div>
          <div className="relative flex items-center gap-2 flex-wrap">
            {/* Installation selector — only if >1 */}
            {installations.length > 1 && (
              <div className="relative">
                <select
                  value={selectedInstId ?? ''}
                  onChange={e => setSelectedInstId(Number(e.target.value))}
                  className="sn-select appearance-none h-9 pl-3 pr-9 text-sm font-medium text-white bg-white/10 hover:bg-white/15 rounded-xl ring-1 ring-inset ring-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70 cursor-pointer transition-colors"
                >
                  {installations.map(inst => (
                    <option key={inst.id} value={inst.id}>{inst.name}</option>
                  ))}
                </select>
                <SnIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-100"><path d="M6 9l6 6 6-6" /></SnIcon>
              </div>
            )}
            <div className="inline-flex items-center h-9 p-1 rounded-xl bg-white/10 ring-1 ring-inset ring-white/20">
              <button onClick={expandAll} className={SEG_BTN}>
                <SnIcon className="w-3.5 h-3.5"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></SnIcon>
                {t('home_expandAll')}
              </button>
              <span className="mx-0.5 h-4 w-px bg-white/20" aria-hidden />
              <button onClick={collapseAll} className={SEG_BTN}>
                <SnIcon className="w-3.5 h-3.5"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" /></SnIcon>
                {t('home_collapseAll')}
              </button>
            </div>
          </div>
        </div>

        {/* Schema content — horizontal node canvas */}
        <div className="sn-canvas overflow-x-auto">
          <div className="px-6 sm:px-8 py-9">
            {loading ? (
              <div className="flex items-center min-w-max" aria-busy="true">
                {[220, 184, 264].map((w, i) => (
                  <div key={w} className="flex items-center">
                    {i > 0 && <div className="sn-skel w-14 h-[2px] rounded-full" />}
                    <div className="sn-skel h-[72px] rounded-2xl animate-pulse" style={{ width: w }} />
                  </div>
                ))}
              </div>
            ) : !currentInst ? (
              <div className="text-center py-8" style={accentVars('installation')}>
                <span className="sn-icon sn-icon-lg mx-auto mb-4">
                  <SnIcon><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" /></SnIcon>
                </span>
                <p className="sn-fg font-semibold">{t('home_noInstallConfigured')}</p>
                <button onClick={() => navigate('/building-architecture')} className="sn-cta mt-4">
                  {t('home_createInstall')}
                </button>
              </div>
            ) : (
              <div className="flex items-start min-w-max">
                {/* === LEVEL 1: Installation === */}
                <div className="sn-card is-root sn-enter shrink-0 h-[72px] min-w-[220px] flex items-center gap-3 pl-3 pr-5" style={accentVars('installation')}>
                  <span className="sn-icon">
                    <SnIcon><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5" /></SnIcon>
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="sn-kicker">{t('home_installation')}</span>
                      <SnPill
                        tone={currentInst.status === 'production' ? 'ok' : currentInst.status === 'validated' ? 'info' : 'neutral'}
                        label={currentInst.status === 'production' ? t('status_production') : currentInst.status === 'validated' ? t('status_validated') : t('status_draft')}
                      />
                    </div>
                    <p className="sn-title mt-1 text-[15px] truncate max-w-[220px]">{currentInst.name}</p>
                  </div>
                </div>

                {/* Connector → Linky */}
                <SchemaFanout centers={[CARD_CENTER]} width={56} from={accentColor('installation')} to={accentColor('linky')} idKey="inst-linky" />

                {/* === LEVEL 2: Linky (Compteur EDF, toujours présent) === */}
                <div className="sn-card sn-enter shrink-0 h-[72px] min-w-[184px] flex items-center gap-3 pl-3 pr-5" style={{ ...accentVars('linky'), animationDelay: '60ms' }}>
                  <span className="sn-icon">
                    <SnIcon><path d="M13 3L4 14h7l-1 7 9-11h-7l1-7z" /></SnIcon>
                  </span>
                  <div>
                    <p className="sn-kicker">{t('home_meter')}</p>
                    <div className="mt-1 flex items-baseline gap-2">
                      <p className="sn-title text-[15px]">Linky</p>
                      <span className="sn-muted text-[11px] font-semibold tabular-nums">12 kVA</span>
                    </div>
                  </div>
                </div>

                {/* === LEVEL 3: Tableau Électrique (click to expand WEMs + Zones, click Zone to expand equip) === */}
                {(() => {
                  const tableauZones = currentInst.zones.filter(z => z.name.startsWith('Tableau'));
                  const regularZones = currentInst.zones.filter(z => !z.name.startsWith('Tableau'));
                  const tableauKey = `tableau-${currentInst.id}`;
                  const isTableauExpanded = expanded.has(tableauKey);
                  const tableauWemSns = tableauZones.flatMap(z =>
                    (z.wattelemSn || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
                  );
                  const tableauWems = devices.filter(d => tableauWemSns.includes(d.device_sn.toLowerCase()));
                  const tableauEqs = tableauZones.flatMap(z => z.equipment || []);

                  // Zones linked to a specific WEM (via wattelemSn) — the core link
                  const zonesForWem = (wem: typeof tableauWems[number]) => regularZones.filter(z => {
                    const sns = (z.wattelemSn || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
                    return sns.includes(wem.device_sn.toLowerCase());
                  });
                  // Zones not attached to any WEM of this tableau → shown straight under the tableau
                  const orphanZones = regularZones.filter(z => {
                    const sns = (z.wattelemSn || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
                    return !tableauWems.some(w => sns.includes(w.device_sn.toLowerCase()));
                  });

                  // Accurate row heights so the connectors stay aligned even when rows expand
                  const zoneRowH = (zone: typeof regularZones[number]): number => {
                    const ne = (zone.equipment || []).length;
                    if (!expanded.has(`zone-${zone.id}`) || ne === 0) return CARD_H;
                    return Math.max(CARD_H, 2 * EQ_OFFSET + ne * EQ_H + (ne - 1) * EQ_GAP);
                  };
                  const wemRowH = (wem: typeof tableauWems[number]): number => {
                    const mz = zonesForWem(wem);
                    if (!expanded.has(`wem-${wem.device_sn}`) || mz.length === 0) return CARD_H;
                    return Math.max(CARD_H, mz.reduce((s, z, i) => s + zoneRowH(z) + (i > 0 ? ROW_GAP : 0), 0));
                  };

                  // Reusable zone renderer (zone card + its equipment fan-out)
                  const renderZone = (zone: typeof regularZones[number], rowIdx = 0) => {
                    const zoneKey = `zone-${zone.id}`;
                    const isZoneExpanded = expanded.has(zoneKey);
                    const zoneEquipment = zone.equipment || [];
                    const eqCenters = zoneEquipment.map((_, i) => EQ_OFFSET + i * (EQ_H + EQ_GAP) + EQ_H / 2);
                    return (
                      <div key={`zone-row-${zone.id}`} className="flex items-start sn-enter" style={{ animationDelay: `${rowIdx * 50}ms` }}>
                        <button
                          onClick={() => toggle(zoneKey)}
                          aria-expanded={zoneEquipment.length > 0 ? isZoneExpanded : undefined}
                          className={`sn-card is-interactive shrink-0 h-[72px] min-w-[228px] flex items-center gap-3 pl-3 pr-3 ${isZoneExpanded && zoneEquipment.length > 0 ? 'is-open' : ''}`}
                          style={accentVars('zone')}
                        >
                          <span className="sn-icon">
                            <SnIcon><path d="M3 21V8l9-5 9 5v13M9 21V12h6v9" /></SnIcon>
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="sn-kicker">{t('home_zone')}</p>
                            <p className="sn-title mt-0.5 text-sm truncate">{displayDefaultName(zone.name, t, tp)}</p>
                            <p className="sn-muted text-[11px] leading-4 tabular-nums">{tp('count_equipment', zoneEquipment.length)}</p>
                          </div>
                          {zoneEquipment.length > 0 && <SnChevron />}
                        </button>

                        {isZoneExpanded && zoneEquipment.length > 0 && (
                          <>
                            <SchemaFanout
                              centers={eqCenters}
                              width={72}
                              from={accentColor('zone')}
                              to={zoneEquipment.map(eq => accentColor(equipAccent(getEquipType(eq))))}
                              live={zoneEquipment.map(eq => getEquipStatus(eq) !== 'offline')}
                              idKey={`eq-${zone.id}`}
                            />
                            <div className="flex flex-col shrink-0" style={{ gap: EQ_GAP, paddingTop: EQ_OFFSET }}>
                              {zoneEquipment.map((eq, eqIdx) => {
                                const status = getEquipStatus(eq);
                                const eqType = getEquipType(eq);
                                return (
                                  <div
                                    key={`zeq-${eq.id ?? eqIdx}`}
                                    onClick={() => {
                                      if (eq.deviceId) navigate(`/wattel-em?deviceId=${eq.deviceId}`);
                                      else navigate(`/building-architecture?installationId=${currentInst.id}`);
                                    }}
                                    className="sn-card is-interactive sn-enter shrink-0 h-[60px] min-w-[236px] flex items-center gap-2.5 pl-2.5 pr-3"
                                    style={{ ...accentVars(equipAccent(eqType)), animationDelay: `${eqIdx * 40}ms` }}
                                  >
                                    <span className="sn-code">{equipCode(eqType)}</span>
                                    <div className="min-w-0 flex-1">
                                      <p className="sn-title text-[12.5px] leading-4 truncate">{displayDefaultName(eq.name, t, tp) || t('home_equipmentN', { n: eqIdx + 1 })}</p>
                                      <p className="sn-accent-text mt-1 text-[10.5px] font-semibold leading-3">{eqTypeLabel(eqType)}</p>
                                    </div>
                                    <SnPill
                                      tone={status === 'online' ? 'ok' : status === 'offline' ? 'err' : 'neutral'}
                                      label={status === 'online' ? t('status_online') : status === 'offline' ? t('status_offline') : '—'}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  };

                  return (
                    <div className="flex items-start shrink-0">
                      {/* Connector Linky→Tableau */}
                      <SchemaFanout centers={[CARD_CENTER]} width={56} from={accentColor('linky')} to={accentColor('tableau')} idKey="linky-tab" />

                      {/* Tableau button */}
                      <button
                        onClick={() => toggle(tableauKey)}
                        aria-expanded={isTableauExpanded}
                        className={`sn-card is-interactive sn-enter shrink-0 h-[72px] min-w-[264px] flex items-center gap-3 pl-3 pr-3 ${isTableauExpanded ? 'is-open' : ''}`}
                        style={{ ...accentVars('tableau'), animationDelay: '120ms' }}
                      >
                        <span className="sn-icon">
                          <SnIcon><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 7v3M12 7v3M16 7v3M8 15h8" /></SnIcon>
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="sn-kicker">{t('home_panel')}</p>
                          <p className="sn-title mt-0.5 text-sm truncate">
                            {tableauZones.length > 0 ? tableauZones.map(z => displayDefaultName(z.name, t, tp)).join(' + ') : t('home_panelTitle')}
                          </p>
                          <p className="sn-muted text-[11px] leading-4 tabular-nums">
                            {tableauWems.length} WattelEM · {tp('count_zone', regularZones.length)} · {tp('count_equipShort', tableauEqs.length)}
                          </p>
                        </div>
                        <SnChevron />
                      </button>

                      {/* === Tableau expanded → WEM rows (each WEM shows ITS OWN zones) === */}
                      {isTableauExpanded && (tableauWems.length > 0 || tableauEqs.length > 0 || orphanZones.length > 0) && (() => {
                        type Item =
                          | { kind: 'wem'; data: typeof tableauWems[number] }
                          | { kind: 'zone'; data: typeof regularZones[number] }
                          | { kind: 'eq'; data: typeof tableauEqs[number] };
                        const items: Item[] = [
                          ...tableauWems.map(w => ({ kind: 'wem' as const, data: w })),
                          ...orphanZones.map(z => ({ kind: 'zone' as const, data: z })),
                          ...tableauEqs.map(e => ({ kind: 'eq' as const, data: e })),
                        ];
                        // Exact vertical center of each row's card (rows grow when expanded)
                        let acc = 0;
                        const centers = items.map(it => {
                          const c = acc + CARD_CENTER;
                          const h = it.kind === 'wem' ? wemRowH(it.data)
                            : it.kind === 'zone' ? zoneRowH(it.data)
                            : CARD_H;
                          acc += h + ROW_GAP;
                          return c;
                        });
                        const itemColors = items.map(it => accentColor(it.kind === 'wem' ? 'wem' : it.kind === 'zone' ? 'zone' : 'tableau-eq'));
                        const itemLive = items.map(it =>
                          it.kind === 'wem' ? !!it.data.online : it.kind === 'eq' ? getEquipStatus(it.data) !== 'offline' : true
                        );
                        return (
                          <>
                            <SchemaFanout centers={centers} width={72} from={accentColor('tableau')} to={itemColors} live={itemLive} idKey={`tab-${currentInst.id}`} />

                            <div className="flex flex-col shrink-0" style={{ gap: ROW_GAP }}>
                              {items.map((item, idx) => {
                                if (item.kind === 'wem') {
                                  const wem = item.data;
                                  const myZones = zonesForWem(wem);
                                  const wKey = `wem-${wem.device_sn}`;
                                  const isWExp = expanded.has(wKey);
                                  let za = 0;
                                  const zCenters = myZones.map(z => { const c = za + CARD_CENTER; za += zoneRowH(z) + ROW_GAP; return c; });
                                  return (
                                    <div key={`wem-${wem.device_sn}`} className="flex items-start sn-enter" style={{ animationDelay: `${idx * 50}ms` }}>
                                      <div
                                        onClick={() => { if (myZones.length > 0) toggle(wKey); }}
                                        title={myZones.length > 0 ? t('home_linkedZones', { n: myZones.length }) : t('home_noLinkedZone')}
                                        className={`sn-card shrink-0 h-[72px] min-w-[248px] flex items-center gap-3 pl-3 pr-3 ${myZones.length > 0 ? 'is-interactive' : ''} ${isWExp && myZones.length > 0 ? 'is-open' : ''}`}
                                        style={accentVars('wem')}
                                      >
                                        <span className="sn-icon">
                                          <SnIcon><path d="M4.5 17a8.5 8.5 0 1 1 15 0" /><path d="M12 14l3.5-4" /><circle cx="12" cy="14" r="1.2" /></SnIcon>
                                        </span>
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="sn-kicker">WattelEM</span>
                                            <SnPill tone={wem.online ? 'ok' : 'err'} label={wem.online ? t('status_online') : t('status_offline')} ping />
                                          </div>
                                          <p className="sn-title mt-1 font-mono text-[12.5px] truncate">{displaySn(wem.device_sn)}</p>
                                        </div>
                                        {myZones.length > 0 && (
                                          <div className="flex items-center gap-1.5 shrink-0 pl-1">
                                            <span className="sn-muted text-[10.5px] font-semibold tabular-nums">{tp('count_zone', myZones.length)}</span>
                                            <SnChevron />
                                          </div>
                                        )}
                                      </div>

                                      {isWExp && myZones.length > 0 && (
                                        <>
                                          <SchemaFanout centers={zCenters} width={72} from={accentColor('wem')} to={accentColor('zone')} live={myZones.map(() => !!wem.online)} idKey={`wemz-${wem.device_sn}`} />
                                          <div className="flex flex-col shrink-0" style={{ gap: ROW_GAP }}>
                                            {myZones.map((z, i) => renderZone(z, i))}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  );
                                }
                                if (item.kind === 'zone') {
                                  return renderZone(item.data, idx);
                                }
                                const eq = item.data;
                                const eqType = getEquipType(eq);
                                const status = getEquipStatus(eq);
                                return (
                                  <div
                                    key={`teq-${eq.id ?? idx}`}
                                    onClick={() => { if (eq.deviceId) navigate(`/wattel-em?deviceId=${eq.deviceId}`); }}
                                    className="sn-card is-interactive sn-enter shrink-0 h-[72px] min-w-[248px] flex items-center gap-3 pl-3 pr-3"
                                    style={{ ...accentVars('tableau-eq'), animationDelay: `${idx * 50}ms` }}
                                  >
                                    <span className="sn-code sn-code-lg">EQ</span>
                                    <div className="min-w-0 flex-1">
                                      <p className="sn-kicker">{t('home_equipmentType')}</p>
                                      <p className="sn-title mt-0.5 text-sm truncate">{displayDefaultName(eq.name, t, tp) || t('home_equipShortN', { n: idx + 1 })}</p>
                                      <p className="sn-muted text-[11px] leading-4">{eqTypeLabel(eqType)}</p>
                                    </div>
                                    <SnPill
                                      tone={status === 'online' ? 'ok' : status === 'offline' ? 'err' : 'neutral'}
                                      label={status === 'online' ? t('status_online') : status === 'offline' ? t('status_offline') : '—'}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="sn-bar flex flex-wrap items-center gap-x-4 gap-y-2 px-5 sm:px-6 py-3 text-[11px] font-medium">
          {([
            ['installation', t('home_installation')],
            ['linky', t('home_meter')],
            ['tableau', t('home_panel')],
            ['wem', 'WattelEM'],
            ['zone', t('home_zone')],
          ] as [AccentKey, string][]).map(([k, label]) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-[3px]" style={{ background: accentColor(k) }} />
              {label}
            </span>
          ))}
          <span className="sn-bar-sep hidden sm:block h-3 w-px" aria-hidden />
          <span className="inline-flex items-center gap-1.5">
            <svg width="22" height="6" aria-hidden><path className="sn-flow" d="M 1 3 H 21" strokeWidth="2" strokeLinecap="round" style={{ stroke: accentColor('installation') }} /></svg>
            {t('home_flowActive')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <svg width="22" height="6" aria-hidden><path className="sn-wire-off" d="M 1 3 H 21" strokeWidth="1.5" strokeDasharray="2 5" strokeLinecap="round" /></svg>
            {t('status_offline')}
          </span>
        </div>
      </div>

      {/* ============ CONSOMMATION PAR ZONE ============ */}
      <ZoneConsumption installations={installations} energy={energy} />
    </div>
  );
}

export default Dashboard;
